import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDate } from "@/lib/format";

// ─────────────────────────────────────────────
// ReviewerControls
//
// Per-question reviewer controls: score (1-5), comment textarea, status indicator.
// Design: Screen 14 — Assessment Review question cards
// ─────────────────────────────────────────────

type ReviewStatus = "NOT_STARTED" | "REVIEWED" | "NEEDS_REVISION";

export interface ReviewCommentData {
  id?: string;
  questionId?: string;
  comment: string;
  score: number | null;
  status: ReviewStatus;
  reviewerName?: string;
  createdAt?: string;
}

interface ReviewerControlsProps {
  assessmentId: string;
  questionId: string;
  responseId: string | null;
  existingReview: ReviewCommentData | null;
  onReviewSaved?: (review: ReviewCommentData) => void;
}

const STATUS_LABEL_KEYS: Record<ReviewStatus, string> = {
  NOT_STARTED: "status.notStarted",
  REVIEWED: "assessment.review.statusReviewed",
  NEEDS_REVISION: "assessment.review.statusNeedsRevision",
};

const STATUS_COLORS: Record<ReviewStatus, { bg: string; text: string }> = {
  NOT_STARTED: { bg: "var(--bg-hover)", text: "var(--text-muted)" },
  REVIEWED: { bg: "var(--success-bg)", text: "var(--success)" },
  NEEDS_REVISION: { bg: "var(--warning-bg)", text: "var(--warning)" },
};

export function ReviewerControls({
  assessmentId,
  questionId: _questionId,
  responseId,
  existingReview,
  onReviewSaved,
}: ReviewerControlsProps) {
  const queryClient = useQueryClient();
  const { t, language } = useTranslation();
  const [comment, setComment] = useState(existingReview?.comment ?? "");
  const [score, setScore] = useState<number | null>(existingReview?.score ?? null);
  const [status, setStatus] = useState<ReviewStatus>(existingReview?.status ?? "NOT_STARTED");
  const [isEditing, setIsEditing] = useState(!existingReview);

  const mutation = useMutation({
    mutationFn: (body: { comment: string; score?: number; status: ReviewStatus }) => {
      if (!responseId) {
        return Promise.reject(new Error(t("assessment.review.noResponseError")));
      }
      return api.patch<{ data: ReviewCommentData }>(
        `/assessments/${assessmentId}/responses/${responseId}/review`,
        body
      );
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["assessment-review", assessmentId] });
      toast.success(t("assessment.review.reviewSaved"));
      setIsEditing(false);
      onReviewSaved?.(res.data);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? t("assessment.review.saveFailed"));
    },
  });

  function handleSave() {
    mutation.mutate({
      comment: comment.trim(),
      ...(score !== null && score !== undefined ? { score } : {}),
      status,
    });
  }

  const currentStatus = existingReview?.status ?? "NOT_STARTED";
  const colors = STATUS_COLORS[currentStatus];

  return (
    <div
      className="mt-3 rounded-card border border-border-default p-4"
      style={{ backgroundColor: "var(--bg-hover)" }}
    >
      {/* Status indicator */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-[13px] font-semibold text-text-muted">{t("assessment.review.reviewLabel")}</span>
        <span
          className="rounded-badge px-2 py-0.5 text-[11px] font-semibold"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {t(STATUS_LABEL_KEYS[currentStatus])}
        </span>
      </div>

      {/* Existing review (read mode) */}
      {!isEditing && existingReview && (
        <div className="flex flex-col gap-2">
          {existingReview.score !== null && existingReview.score !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-text-muted">{t("assessment.score")}:</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span
                    key={n}
                    className="flex h-6 w-6 items-center justify-center rounded text-[12px] font-semibold"
                    style={{
                      backgroundColor: n <= existingReview.score! ? "var(--accent-bg)" : "var(--bg-page)",
                      color: n <= existingReview.score! ? "var(--accent-text)" : "var(--text-muted)",
                    }}
                  >
                    {n}
                  </span>
                ))}
              </div>
            </div>
          )}
          {existingReview.comment && (
            <p className="text-[13px] text-text-secondary">{existingReview.comment}</p>
          )}
          {existingReview.reviewerName && (
            <p className="text-[12px] text-text-muted">
              - {existingReview.reviewerName}
              {existingReview.createdAt && (
                <span> · {formatDate(existingReview.createdAt, language)}</span>
              )}
            </p>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsEditing(true)}
            className="self-start"
          >
            {t("common.edit")}
          </Button>
        </div>
      )}

      {/* Edit mode */}
      {isEditing && (
        <div className="flex flex-col gap-3">
          {/* Score selector (1-5) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-text-secondary">{t("assessment.review.scoreRange")}</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setScore(score === n ? null : n)}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-control text-[13px] font-semibold transition-colors",
                    "border border-border-default hover:border-accent"
                  )}
                  style={{
                    backgroundColor: score === n ? "var(--accent-bg)" : "var(--bg-input)",
                    color: score === n ? "var(--accent-text)" : "var(--text-muted)",
                    borderColor: score === n ? "var(--accent)" : undefined,
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-text-secondary">{t("assessment.review.commentLabel")}</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("assessment.review.commentPlaceholder")}
              rows={3}
              className="min-h-[70px] w-full rounded-control border border-border-default bg-bg-input px-3 py-2 text-[13px] text-text-primary placeholder:text-text-muted resize-y focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
            />
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[13px] font-medium text-text-secondary">{t("assessment.status")}</label>
            <div className="flex gap-2">
              {(["REVIEWED", "NEEDS_REVISION"] as ReviewStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className="rounded-badge px-3 py-1 text-[12px] font-semibold transition-colors"
                  style={{
                    backgroundColor: status === s ? STATUS_COLORS[s].bg : "var(--bg-page)",
                    color: status === s ? STATUS_COLORS[s].text : "var(--text-muted)",
                    border: `1px solid ${status === s ? STATUS_COLORS[s].text : "var(--border-default)"}`,
                  }}
                >
                  {t(STATUS_LABEL_KEYS[s])}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              isLoading={mutation.isPending}
            >
              {t("assessment.review.saveReview")}
            </Button>
            {existingReview && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setComment(existingReview.comment);
                  setScore(existingReview.score);
                  setStatus(existingReview.status);
                }}
                disabled={mutation.isPending}
              >
                {t("common.cancel")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
